import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import judicatures from '@/assets/judicatures.json';
import { createCase, getCases } from '@/lib/recepthor-api';
import { buildRol } from './causaDisplay';
import { mapOrgMembers } from '@/lib';
import { extractList } from '@/lib/apiList';
import { qk } from '@/lib/queryKeys';
import { useAuth } from '@/app/AuthContext.jsx';
import { useCanAssignCase, useMembers } from '@/hooks';
import { useToast } from '@/ui';

// The product processes civil causes only, so competencia (cod 3) is fixed and
// pre-selected. The book defaults to C (the main civil book) but stays
// selectable: the API validates any code of its LabelValue catalog.
export const FIXED_COMPETENCIA = { cod: '3', label: 'Civil' };
export const DEFAULT_LIBRO = 'C';

// The subscription 404 comes from the API after the causa is created (the
// PJUD verification is asynchronous), so it says nothing about the PJUD: only
// that the subscription was not confirmed. The message gives the way out
// (write to us) and the form detects this text to offer the contacts next to it.
export const SUBSCRIPTION_UNCONFIRMED_ERROR =
  'No pudimos confirmar la suscripción de esta causa. Escríbenos para revisarla.';

export const ALREADY_FOLLOWED_ERROR =
  'El caso ya está siendo seguido por la organización.';

// Product scope of the book select, narrower than the catalog the API accepts
// (`assets/bookTypes.json`, mirror of its LabelValue table): only the books
// whose proceedings need a receptor to notify or to execute a diligencia. The
// remaining SICA codes stay valid on the API for causas the scraper brings in.
export const OFFERED_LIBROS = ['C', 'E', 'A'];

export function emptyNuevaCausaValues() {
  return {
    competencia: FIXED_COMPETENCIA.cod,
    corte: '',
    tribunal: '',
    libro: DEFAULT_LIBRO,
    rol: '',
    anio: String(new Date().getFullYear()),
    encargadoId: '',
  };
}

// Shared data + state + submit pipeline for the "Nueva causa" form, used by
// SuscribirCausaModal from both Causas and Dashboard (REC-1792). The form
// VALUES live here (not inside NuevaCausa) so they survive the modal
// unmounting on close — closing without submitting keeps the input; only
// `resetForm` (the clear icon) or a successful create wipes it. The form
// stores the numeric "cod" (PJUD value) for corte/tribunal, so the submit
// needs no label→code lookup. `onCreated` fires after a successful
// createCase: each caller closes its own modal; the refresh itself runs
// below in handleSubmit (invalidateAfterCreateCausa), shared by every
// caller rather than repeated per caller.
// Inicio's "Causas activas" tile (useDashboardInicio) has no poll of its
// own, so without this it keeps the count from before a create until a
// reload or a window-focus refetch. It runs here, in the one submit every
// SuscribirCausaModal caller shares, so neither entry point can skip it.
// The tile reads a plain aggregate with no per-case syncing signal, so it
// cannot condition a poll the way useCasesList does; invalidating it on the
// same event useCasesList already reacts to is the smallest fix that keeps
// it in sync without one.
export function invalidateAfterCreateCausa(queryClient) {
  queryClient.invalidateQueries({ queryKey: qk.cases.all() });
  // qk.dashboardInicio(params) always carries a params object as its second
  // element, so invalidating with a literal `undefined` there would not
  // match any cached entry (RQ compares element by element, and
  // `undefined !== {...}`); the bare one-element prefix is what matches.
  queryClient.invalidateQueries({ queryKey: ['dashboard-inicio'] });
}

export function useNuevaCausaForm({ onCreated } = {}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { organizationId } = useAuth();
  // Only case:assign holders see the "Encargado" field (USER-0010); the members
  // endpoint is admin-gated anyway, so skip the query for everyone else.
  const canAssign = useCanAssignCase();
  const [apiError, setApiError] = useState(null);
  // What was sent when it failed (rol, tribunal by its label and, on a 409, the
  // causa already followed), so the error does not change if the form is edited.
  const [failedCase, setFailedCase] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [values, setValues] = useState(emptyNuevaCausaValues);

  // Org members for the optional "Encargado" select.
  const { data: membersData } = useMembers(organizationId, {
    enabled: organizationId != null && canAssign,
  });
  const members = useMemo(
    () => (membersData ? mapOrgMembers(extractList(membersData)) : []),
    [membersData],
  );

  // Cortes as `{ cod, label }`; cod is the PJUD `value`.
  const cortes = useMemo(
    () => judicatures.map((j) => ({ cod: String(j.value), label: j.label })),
    [],
  );

  // `{ [corteCod]: [{ cod, label }] }` so the tribunal list keys off the
  // selected corte's cod (which is what the form stores).
  const tribunalesPorCorte = useMemo(() => {
    const map = {};
    for (const j of judicatures) {
      map[String(j.value)] = (j.tribunals ?? []).map((t) => ({
        cod: String(t.value),
        label: t.label,
      }));
    }
    return map;
  }, []);

  const setField = useCallback((field, value) => {
    // Editing the form makes the error for what was sent before stale.
    setApiError(null);
    setFailedCase(null);
    setValues((prev) => {
      const next = { ...prev, [field]: value };
      // Picking a different corte invalidates the tribunal choice.
      if (field === 'corte') next.tribunal = '';
      return next;
    });
  }, []);

  const resetForm = useCallback(() => {
    setValues(emptyNuevaCausaValues());
    setApiError(null);
    setFailedCase(null);
  }, []);

  const resetError = useCallback(() => {
    setApiError(null);
    setFailedCase(null);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      setApiError(null);
      setFailedCase(null);
      setIsSubmitting(true);
      // What was sent, with the tribunal by its catalog label.
      const sent = {
        rol: `${data.libro}-${data.rol}-${data.anio}`,
        tribunal:
          (tribunalesPorCorte[data.corte] ?? []).find(
            (t) => t.cod === data.tribunal,
          )?.label ?? null,
      };
      try {
        const created = await createCase({
          competence: data.competencia, // fixed '3' (civil)
          courtCode: data.corte, // corte cod
          tribunalCode: data.tribunal, // tribunal cod
          bookType: data.libro, // selected book code
          rol: data.rol,
          year: data.anio,
          // Create-time encargado (requires case:assign; the field is hidden
          // otherwise). Omitted → the API applies the default (solo orgs
          // auto-assign the owner).
          ...(data.encargadoId ? { assigneeId: data.encargadoId } : {}),
        });
        invalidateAfterCreateCausa(queryClient);
        // Wipe the form after a successful suscripción so the next open starts
        // clean (closing WITHOUT submitting keeps the input — see resetForm).
        setValues(emptyNuevaCausaValues());
        // The caller closes its own modal and invalidateAfterCreateCausa
        // above already refreshed the lists, so a plain confirmation is
        // enough — no "Ver" action needed. Errors stay inline (see catch) to
        // keep the message next to the form fields, not double-announced in
        // a toast.
        toast({
          kind: 'success',
          message: created?.rol
            ? `Causa ${created.rol} creada.`
            : 'Causa creada.',
        });
        // Hand the created case back so the route can open its drawer at once.
        onCreated?.(created);
      } catch (err) {
        if (err?.status === 409) {
          setApiError(ALREADY_FOLLOWED_ERROR);
          // The 409 carries no causa id: it is looked up with the same search as
          // the picker (without `active`, so it includes not-found ones) and
          // a link is offered only when a single row matches the rol (the
          // tribunal only breaks ties between several).
          let caseId = null;
          try {
            const res = await getCases({
              page: 1,
              pageSize: 20,
              search: sent.rol,
              organizationId,
            });
            const list = Array.isArray(res?.results) ? res.results : [];
            // A single row with that rol is enough: the catalog's tribunal label
            // does not always equal the API name (case, abbreviations), so it
            // only breaks ties when there are several.
            const byRol = list.filter((c) => buildRol(c) === sent.rol);
            const same =
              byRol.length > 1 && sent.tribunal != null
                ? byRol.filter((c) => c.tribunalName === sent.tribunal)
                : byRol;
            if (same.length === 1)
              caseId = same[0].caseId ?? same[0].id ?? null;
          } catch {
            // Without the lookup, the text stays, with no link.
          }
          setFailedCase({ ...sent, existingCaseId: caseId });
        } else if (err?.status === 404) {
          setApiError(SUBSCRIPTION_UNCONFIRMED_ERROR);
          setFailedCase(sent);
        } else {
          setApiError('Error al crear la causa. Intenta nuevamente.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [onCreated, organizationId, queryClient, toast, tribunalesPorCorte],
  );

  return {
    canAssign,
    cortes,
    tribunalesPorCorte,
    members,
    values,
    setField,
    resetForm,
    resetError,
    handleSubmit,
    apiError,
    failedCase,
    isSubmitting,
  };
}

export default useNuevaCausaForm;
